import {
  ApplicationInsights,
  SeverityLevel,
} from "@microsoft/applicationinsights-web";
import { env } from "../infrastructure/environment";
import { getCurrentTenantFromStorage } from "../infrastructure/tenant";

let appInsightsInstance: ApplicationInsights | null = null;

export default async function getAzureApplicationInsights() {
  if (appInsightsInstance) {
    return appInsightsInstance;
  }

  const currentTenant = await getCurrentTenantFromStorage();
  const logToConsole = localStorage.getItem("logToConsole") === "true";

  appInsightsInstance = new ApplicationInsights({
    config: {
      connectionString: env.APPLICATION_INSIGHTS_CONNECTIONSTRING,
      accountId: currentTenant?.id,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      distributedTracingMode: 2,
      enableAjaxErrorStatusText: true,
      enableAutoRouteTracking: true,
      autoTrackPageVisitTime: true,
      correlationHeaderDomains: ["*.casablanca.at"],
      correlationHeaderExcludedDomains: [],
      disableAjaxTracking: true,
      disableFetchTracking: true,
      loggingLevelTelemetry: env.MINIMUM_LOGLEVEL,
    },
  });

  // Add custom logging
  if (logToConsole) {
    appInsightsInstance.addTelemetryInitializer((envelope) => {
      const { name, baseType, baseData } = envelope;
      let logMessage = "";

      if (baseData) {
        if (baseData.message) {
          logMessage += baseData.message;
        } else if (baseData.exceptions && baseData.exceptions.length > 0) {
          logMessage += baseData.exceptions[0].message;
        } else if (baseData.url) {
          logMessage += `URL - ${baseData.url}`;
        } else if (baseData.name) {
          logMessage += baseData.name;
        } else if (baseData) {
          logMessage += baseData;
        }
      }

      // Use severity if available, otherwise default to info
      const severity = baseData?.severityLevel ?? SeverityLevel.Information;
      switch (severity) {
        case SeverityLevel.Error:
          console.error(logMessage);
          break;
        case SeverityLevel.Warning:
          console.warn(logMessage);
          break;
        case SeverityLevel.Information:
          console.info(logMessage);
          break;
        case SeverityLevel.Verbose:
          console.debug(logMessage);
          break;
        default:
          console.log(logMessage);
      }
      return true;
    });
  }

  appInsightsInstance.loadAppInsights();
  window.appInsights = appInsightsInstance;

  // Send initial telemetry to create an operation ID (dont delete this because it enshures e always have an operation ID)
  appInsightsInstance.trackEvent({
    name: "ApplicationStarted",
    properties: {
      phase: "initialization",
    },
  });

  // Add performance metrics event listener
  (window as any).reportPagePerformance = (performanceData: {
    timeToInteractiveMs?: number;
    dataFetchMs?: number;
    renderMs?: number;
  }) => {
    // Get current page view context
    const currentUrl = window.location.href;
    const pageName = window.location.pathname;
    const pageTitle = document?.title || pageName;

    // Prepare measurements (numeric metrics) - only include if they have values
    const measurements: Record<string, number> = {};
    if (performanceData.timeToInteractiveMs !== undefined) {
      measurements.timeToInteractiveMs = performanceData.timeToInteractiveMs;
    }
    if (performanceData.dataFetchMs !== undefined) {
      measurements.dataFetchMs = performanceData.dataFetchMs;
    }
    if (performanceData.renderMs !== undefined) {
      measurements.renderMs = performanceData.renderMs;
    }

    // Only proceed if we have performance data to report
    if (Object.keys(measurements).length > 0) {
      // Send as a custom event so it doesn't count as a PageView
      appInsightsInstance.trackEvent({
        name: "PagePerformance",
        properties: {
          title: pageTitle,
          route: pageName,
          uri: currentUrl,
          refUri: document.referrer || undefined,
        },
        measurements,
      });
    }
  };

  return appInsightsInstance;
}
